import React, { useEffect, useState, useCallback } from 'react';
import { Save, Upload, Store, Lock, Loader2, Users, UserPlus, Trash2, Shield } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const ROLE_LABEL = { owner: 'Owner', staff: 'Staff / Admin', field: 'Tukang / Lapangan' };
const ROLE_STYLE = {
  owner: 'border-primary/30 bg-primary/10 text-primary',
  staff: 'border-sky-200 bg-sky-50 text-sky-700',
  field: 'border-amber-200 bg-amber-50 text-amber-800',
};

function TeamSection({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nama: '', email: '', password: '', role: 'staff' });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/users'); setUsers(r.data); } catch { /* abaikan kegagalan muat */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addUser = async () => {
    if (!form.nama.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error('Lengkapi nama, email, dan kata sandi (min. 6 karakter).');
      return;
    }
    setSaving(true);
    try {
      await api.post('/users', form);
      toast.success(`${ROLE_LABEL[form.role]} ditambahkan`);
      setAddOpen(false); setForm({ nama: '', email: '', password: '', role: 'staff' }); load();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal menambah pengguna'); } finally { setSaving(false); }
  };

  const removeUser = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success('Pengguna dihapus'); setToDelete(null); load();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal menghapus'); }
  };

  return (
    <div className="rounded-xl border bg-card p-5 space-y-4" data-testid="team-section">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold"><Users className="h-4 w-4 text-primary" /> Tim / Pengguna</h2>
        <Button size="sm" data-testid="team-add-button" onClick={() => setAddOpen(true)} className="rounded-lg"><UserPlus className="mr-1.5 h-4 w-4" /> Tambah</Button>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">Undang Staff (admin) atau Tukang (lapangan) ke workshop Anda.</p>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const initials = (u.nama || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
            const isSelf = u.id === currentUser?.id;
            return (
              <div key={u.id} data-testid="team-member-row" className="flex items-center gap-3 rounded-xl border bg-background p-3">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-secondary text-xs">{initials}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{u.nama}</span>
                    {isSelf && <span className="text-[10px] text-muted-foreground">(Anda)</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                </div>
                <Badge variant="outline" className={`rounded-full ${ROLE_STYLE[u.role] || ''}`}>{ROLE_LABEL[u.role] || u.role}</Badge>
                {u.role !== 'owner' && !isSelf && (
                  <button data-testid="team-delete-button" onClick={() => setToDelete(u)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" title="Hapus">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Tambah Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nama</Label><Input data-testid="team-form-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Nama lengkap" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="team-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@workshop.com" /></div>
            <div className="space-y-1.5"><Label>Kata Sandi Awal</Label><Input data-testid="team-form-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 karakter" /></div>
            <div className="space-y-1.5">
              <Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="team-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff / Admin (kelola data, tanpa ubah harga)</SelectItem>
                  <SelectItem value="field">Tukang / Lapangan (hanya tugas, tanpa data uang)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-800">
              <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Tukang tidak akan melihat data finansial (nilai, profit, pembayaran).
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="team-form-submit" onClick={addUser} disabled={saving} className="rounded-xl">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Tambah
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus pengguna?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.nama} ({toDelete?.email}) akan kehilangan akses ke workshop Anda. Tindakan ini tidak bisa dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="team-delete-confirm" onClick={removeUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Pengaturan() {
  const { isOwner, user } = useAuth();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings').then((r) => setS(r.data)).finally(() => setLoading(false));
  }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error('Ukuran logo maksimal 1.5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setS({ ...s, shop_logo: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        shop_name: s.shop_name, shop_address: s.shop_address, shop_contact: s.shop_contact,
        shop_logo: s.shop_logo || '', default_margin_pct: Number(s.default_margin_pct) || 0,
        ppn_enabled: !!s.ppn_enabled, ppn_rate: Number(s.ppn_rate) || 0,
        default_dp_pct: Number(s.default_dp_pct) || 0, quote_validity_days: Number(s.quote_validity_days) || 14,
      };
      await api.put('/settings', payload);
      toast.success('Pengaturan disimpan');
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal menyimpan'); } finally { setSaving(false); }
  };

  if (loading || !s) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const disabled = !isOwner;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold">Pengaturan Workshop</h1>
        <p className="text-sm text-muted-foreground">Identitas workshop, default penawaran & tim.</p>
      </div>
      {disabled && (
        <div className="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          <Lock className="h-4 w-4" /> Hanya owner yang dapat mengubah pengaturan.
        </div>
      )}

      <div className="rounded-xl border bg-card p-5 space-y-4">
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold"><Store className="h-4 w-4 text-primary" /> Identitas Workshop</h2>
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted">
            {s.shop_logo ? <img src={s.shop_logo} alt="Logo" className="h-full w-full object-contain" /> : <Store className="h-8 w-8 text-muted-foreground" />}
          </div>
          <div>
            <Label className="mb-1.5 block">Logo Workshop (untuk PDF)</Label>
            <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
              <Upload className="h-4 w-4" /> Unggah Logo
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={disabled} />
            </label>
            <p className="mt-1 text-xs text-muted-foreground">PNG/JPG, maks 1.5MB</p>
          </div>
        </div>
        <div className="space-y-1.5"><Label>Nama Workshop</Label><Input data-testid="pengaturan-nama-toko" value={s.shop_name || ''} disabled={disabled} onChange={(e) => setS({ ...s, shop_name: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Alamat</Label><Textarea value={s.shop_address || ''} disabled={disabled} onChange={(e) => setS({ ...s, shop_address: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Kontak (WA/Telp)</Label><Input value={s.shop_contact || ''} disabled={disabled} onChange={(e) => setS({ ...s, shop_contact: e.target.value })} /></div>
      </div>

      <div className="rounded-xl border bg-card p-5 space-y-4">
        <h2 className="font-display text-sm font-semibold">Default Penawaran</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Margin Default (%)</Label><Input data-testid="pengaturan-margin" type="number" value={s.default_margin_pct ?? 15} disabled={disabled} onChange={(e) => setS({ ...s, default_margin_pct: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>DP Default (%)</Label><Input type="number" value={s.default_dp_pct ?? 50} disabled={disabled} onChange={(e) => setS({ ...s, default_dp_pct: e.target.value })} /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div><div className="text-sm font-medium">Aktifkan PPN</div><div className="text-xs text-muted-foreground">Tambahkan PPN pada penawaran</div></div>
          <Switch data-testid="pengaturan-ppn-toggle" checked={!!s.ppn_enabled} disabled={disabled} onCheckedChange={(v) => setS({ ...s, ppn_enabled: v })} />
        </div>
        {s.ppn_enabled && (
          <div className="space-y-1.5"><Label>Tarif PPN (%)</Label><Input type="number" value={s.ppn_rate ?? 11} disabled={disabled} onChange={(e) => setS({ ...s, ppn_rate: e.target.value })} /></div>
        )}
        <div className="space-y-1.5"><Label>Masa Berlaku Penawaran (hari)</Label><Input type="number" value={s.quote_validity_days ?? 14} disabled={disabled} onChange={(e) => setS({ ...s, quote_validity_days: e.target.value })} /></div>
      </div>

      {!disabled && (
        <Button data-testid="pengaturan-simpan" onClick={save} disabled={saving} className="w-full rounded-xl">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Simpan Pengaturan
        </Button>
      )}

      {isOwner && <TeamSection currentUser={user} />}
    </div>
  );
}
